import React, { useCallback, useEffect, useMemo, useState } from 'react';
import aulaService from '../../services/AulaService';
import './AulaAsistentesModal.css';

const hasOwn = (source, key) => Object.prototype.hasOwnProperty.call(source || {}, key);

const hasReadyFlag = (assistant) => (
  hasOwn(assistant, 'gptClasicoCreacionFinalizada') ||
  hasOwn(assistant, 'GPTClasicoCreacionFinalizada')
);

const normalizeAssistant = (assistant) => ({
  idOpenAIAssistant: assistant?.idOpenAIAssistant || assistant?.IdOpenAIAssistant || '',
  nombre: assistant?.nombre || assistant?.Nombre || '',
  nombreVisible: assistant?.nombreVisible || assistant?.NombreVisible || assistant?.nombre || assistant?.Nombre || 'Asistente IA',
  nombreArea: assistant?.nombreArea || assistant?.NombreArea || '',
  nombreMateria: assistant?.nombreMateria || assistant?.NombreMateria || '',
  gptClasicoUrlPublica: assistant?.gptClasicoURLPublica || assistant?.GPTClasicoURLPublica || '',
  gptClasicoCreacionFinalizada: assistant?.gptClasicoCreacionFinalizada ?? assistant?.GPTClasicoCreacionFinalizada ?? null,
  hasReadyFlag: hasReadyFlag(assistant),
  estaAsignadoAlAula: !!(assistant?.estaAsignadoAlAula ?? assistant?.EstaAsignadoAlAula),
  disponibleParaAsignar: !!(assistant?.disponibleParaAsignar ?? assistant?.DisponibleParaAsignar),
  esAsignadoDirectoAlProfesor: !!(assistant?.esAsignadoDirectoAlProfesor ?? assistant?.EsAsignadoDirectoAlProfesor)
});

const isAssistantReady = (assistant) => (
  !assistant.hasReadyFlag || assistant.gptClasicoCreacionFinalizada === true
);

const normalizeText = (value) => String(value || '').trim().toLowerCase();

const buildSecondaryText = (assistant) => {
  const parts = [assistant.nombreMateria, assistant.nombreArea];
  if (assistant.esAsignadoDirectoAlProfesor) {
    parts.push('Asignado directo');
  }
  return parts.filter(Boolean).join(' · ');
};

const AulaAsistentesPanel = ({ aulaId, service = aulaService }) => {
  const [assistants, setAssistants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyAssistantId, setBusyAssistantId] = useState('');
  const [busyAction, setBusyAction] = useState('');
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');

  const loadAssistants = useCallback(async () => {
    if (!aulaId) return;

    setLoading(true);
    setError('');
    try {
      const response = await service.getAulaAsistentesIA(aulaId);
      const items = response?.asistentes || response?.Asistentes || response?.items || response?.Items || response || [];
      setAssistants(Array.isArray(items) ? items.map(normalizeAssistant).filter(isAssistantReady) : []);
    } catch (e) {
      setError(e.message || 'No pudimos cargar los asistentes IA del aula.');
      setAssistants([]);
    } finally {
      setLoading(false);
    }
  }, [aulaId, service]);

  useEffect(() => {
    loadAssistants();
  }, [loadAssistants]);

  const filteredAssistants = useMemo(() => {
    const term = normalizeText(search);
    if (!term) return assistants;

    return assistants.filter((assistant) => {
      const haystack = [
        assistant.nombreVisible,
        assistant.nombre,
        assistant.nombreArea,
        assistant.nombreMateria,
        assistant.esAsignadoDirectoAlProfesor ? 'asignado directo' : ''
      ].join(' ');

      return normalizeText(haystack).includes(term);
    });
  }, [assistants, search]);

  const assignedAssistants = useMemo(
    () => filteredAssistants.filter((assistant) => assistant.estaAsignadoAlAula),
    [filteredAssistants]
  );

  const availableAssistants = useMemo(
    () => filteredAssistants.filter((assistant) => !assistant.estaAsignadoAlAula && assistant.disponibleParaAsignar),
    [filteredAssistants]
  );

  const unavailableAssignedCount = useMemo(
    () => assignedAssistants.filter((assistant) => !assistant.disponibleParaAsignar).length,
    [assignedAssistants]
  );

  const handleToggleAssignment = async (assistant, assign) => {
    const assistantId = assistant?.idOpenAIAssistant;
    if (!aulaId || !assistantId) return;

    setBusyAssistantId(assistantId);
    setBusyAction(assign ? 'assign' : 'remove');
    setError('');
    try {
      if (assign) {
        await service.addAulaAsistenteIA(aulaId, assistantId);
      } else {
        await service.removeAulaAsistenteIA(aulaId, assistantId);
      }

      await loadAssistants();
    } catch (e) {
      setError(e.message || 'No pudimos actualizar los asistentes IA del aula.');
    } finally {
      setBusyAssistantId('');
      setBusyAction('');
    }
  };

  const renderAssistantList = (items, emptyText, assign) => {
    if (items.length === 0) {
      return (
        <div className="aula-assistants-empty">
          <i className="fas fa-robot" />
          <p>{emptyText}</p>
        </div>
      );
    }

    return (
      <div className="aula-assistants-list">
        {items.map((assistant) => {
          const assistantId = assistant.idOpenAIAssistant;
          const secondaryText = buildSecondaryText(assistant);
          const isBusy = busyAssistantId === assistantId;

          return (
            <div key={assistantId} className="aula-assistants-item">
              <div className="aula-assistants-item-main">
                <div className="aula-assistants-item-header">
                  <strong>{assistant.nombreVisible}</strong>
                  <div className="aula-assistants-badges">
                    {assistant.esAsignadoDirectoAlProfesor && (
                      <span className="aula-assistants-badge info">Asignado directo</span>
                    )}
                    {assistant.estaAsignadoAlAula && (
                      <span className="aula-assistants-badge success">En el aula</span>
                    )}
                    {assistant.estaAsignadoAlAula && !assistant.disponibleParaAsignar && (
                      <span className="aula-assistants-badge warning">Ya no disponible</span>
                    )}
                  </div>
                </div>
                {secondaryText && <p>{secondaryText}</p>}
                {assistant.gptClasicoUrlPublica && (
                  <a
                    href={assistant.gptClasicoUrlPublica}
                    target="_blank"
                    rel="noreferrer"
                    className="aula-assistants-link"
                  >
                    Ver asistente IA
                  </a>
                )}
              </div>
              <div className="aula-assistants-item-actions">
                <button
                  type="button"
                  className={`btn ${assign ? 'btn-primary' : 'btn-secondary'}`}
                  onClick={() => handleToggleAssignment(assistant, assign)}
                  disabled={isBusy}
                >
                  {isBusy && busyAction === (assign ? 'assign' : 'remove') ? (
                    <i className="fas fa-spinner fa-spin" />
                  ) : (
                    <i className={`fas ${assign ? 'fa-link' : 'fa-unlink'}`} />
                  )}
                  <span>{assign ? 'Asociar' : 'Desvincular'}</span>
                </button>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="aula-assistants-panel-root">
      <div className="aula-assistants-toolbar">
        <div className="aula-assistants-search">
          <i className="fas fa-search" aria-hidden="true" />
          <input
            type="text"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Buscar por nombre, materia o área"
          />
        </div>
        <div className="aula-assistants-summary">
          <span>{assignedAssistants.length} asignados</span>
          <span>{availableAssistants.length} disponibles</span>
        </div>
      </div>

      {error && (
        <div className="aula-assistants-error">
          <i className="fas fa-exclamation-triangle" />
          <span>{error}</span>
        </div>
      )}

      <div className="aula-assistants-body">
        {loading ? (
          <div className="aula-assistants-loading">
            <i className="fas fa-spinner fa-spin" />
            <p>Cargando asistentes IA...</p>
          </div>
        ) : (
          <div className="aula-assistants-grid">
            <section className="aula-assistants-panel assigned">
              <div className="aula-assistants-panel-header">
                <div>
                  <h4>Asignados al aula</h4>
                  <p>Podés desvincular cualquiera de los asistentes ya asociados.</p>
                </div>
                <span className="aula-assistants-count">{assignedAssistants.length}</span>
              </div>
              {unavailableAssignedCount > 0 && (
                <div className="aula-assistants-note">
                  {unavailableAssignedCount === 1
                    ? 'Hay 1 asistente asignado que ya no está disponible para nuevas asociaciones.'
                    : `Hay ${unavailableAssignedCount} asistentes asignados que ya no están disponibles para nuevas asociaciones.`}
                </div>
              )}
              {renderAssistantList(
                assignedAssistants,
                'Esta aula todavía no tiene asistentes IA asociados.',
                false
              )}
            </section>

            <section className="aula-assistants-panel available">
              <div className="aula-assistants-panel-header">
                <div>
                  <h4>Disponibles para asociar</h4>
                  <p>Se muestran solo los asistentes IA que hoy se pueden agregar al aula.</p>
                </div>
                <span className="aula-assistants-count">{availableAssistants.length}</span>
              </div>
              {renderAssistantList(
                availableAssistants,
                'No hay asistentes IA disponibles para asociar con los criterios actuales.',
                true
              )}
            </section>
          </div>
        )}
      </div>
    </div>
  );
};

export default AulaAsistentesPanel;
